import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Delete, Coffee, LogIn, LogOut, MonitorSmartphone, Users as UsersIcon, ArrowLeft, MapPin } from "lucide-react";
import Footer from "@/components/Footer";
import { useBranding } from "@/lib/branding";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NUMBERS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "⌫"];

const ACTIONS = [
  { id: "in", label: "Kommen", icon: LogIn, color: "from-emerald-500 to-emerald-600" },
  { id: "break_start", label: "Pause an", icon: Coffee, color: "from-amber-500 to-amber-600" },
  { id: "break_end", label: "Pause aus", icon: Coffee, color: "from-orange-500 to-orange-600" },
  { id: "out", label: "Gehen", icon: LogOut, color: "from-rose-500 to-rose-600" },
];

export default function KioskPage() {
  const [mode, setMode] = useState("start"); // start | location | name | pin
  const [pin, setPin] = useState("");
  const [now, setNow] = useState(new Date());
  const [action, setAction] = useState(null);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [locationId, setLocationId] = useState(() => localStorage.getItem("kiosk_location") || null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [candidates, setCandidates] = useState(null); // for PIN collision
  const [pendingAction, setPendingAction] = useState(null);
  const { branding } = useBranding();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    api.get("/locations").then((r) => {
      setLocations(r.data);
      if (!locationId && r.data.length > 0) {
        setLocationId(r.data[0].id);
        localStorage.setItem("kiosk_location", r.data[0].id);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!locationId) return;
    api.get(`/timeclock/kiosk-users?location_id=${locationId}`).then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, [locationId]);

  const changeLocation = (id) => {
    setLocationId(id);
    localStorage.setItem("kiosk_location", id);
    setMode("start");
    setSelectedUser(null);
    setPin("");
  };

  const handleKey = (k) => {
    if (k === "C") { setPin(""); return; }
    if (k === "⌫") { setPin((p) => p.slice(0, -1)); return; }
    if (pin.length >= 6) return;
    setPin((p) => p + k);
  };

  const submit = async (actionId) => {
    if (pin.length < 4) { toast.error("PIN mindestens 4-stellig"); return; }
    setAction(actionId);
    try {
      const r = await api.post("/timeclock/kiosk", {
        pin, action: actionId, location_id: locationId,
        user_id: selectedUser?.id || null,
      });
      if (r.data.needs_selection) {
        setCandidates(r.data.candidates);
        setPendingAction(actionId);
        return;
      }
      const user = r.data.user;
      const map = { in: "eingestempelt", out: "ausgestempelt", break_start: "Pause gestartet", break_end: "Pause beendet" };
      toast.success(`${user.first_name} ${user.last_name} • ${map[actionId]}`, { duration: 3000 });
      resetToStart();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setTimeout(() => setAction(null), 800);
    }
  };

  const submitWithCandidate = async (userId) => {
    setCandidates(null);
    try {
      const r = await api.post("/timeclock/kiosk", { pin, action: pendingAction, location_id: locationId, user_id: userId });
      const user = r.data.user;
      const map = { in: "eingestempelt", out: "ausgestempelt", break_start: "Pause gestartet", break_end: "Pause beendet" };
      toast.success(`${user.first_name} ${user.last_name} • ${map[pendingAction]}`, { duration: 3000 });
      resetToStart();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const resetToStart = () => {
    setPin("");
    setSelectedUser(null);
    setMode("start");
    setCandidates(null);
    setPendingAction(null);
  };

  const currentLoc = locations.find(l => l.id === locationId);
  const dateStr = now.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const timeStr = now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50/40 to-slate-100 dark:from-slate-950 dark:via-blue-950/20 dark:to-slate-900">
      <Toaster position="top-center" richColors closeButton />

      {/* Header */}
      <div className="flex items-center justify-between px-6 lg:px-8 pt-6">
        <div className="flex items-center gap-3">
          {branding?.logo_b64 ? (
            <img src={branding.logo_b64} alt="logo" className="h-11 w-11 rounded-xl object-contain p-1 bg-white dark:bg-slate-800 border border-border/40" />
          ) : (
            <div className="h-11 w-11 rounded-xl bg-blue-700 text-white grid place-items-center font-bold text-lg">Rx</div>
          )}
          <div>
            <div className="font-heading font-extrabold text-lg">{branding?.system_name || "Apotheken Portal"}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Terminal · Kiosk-Modus</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="kiosk-location-btn"
            onClick={() => setMode(mode === "location" ? "start" : "location")}
            className="flex items-center gap-1.5 text-sm rounded-full bg-white dark:bg-slate-800 border border-border/60 px-3 py-1.5 hover:bg-slate-100"
          >
            <MapPin className="w-4 h-4 text-blue-600" />
            <span className="font-medium">{currentLoc?.name || "Standort wählen"}</span>
          </button>
          <button data-testid="kiosk-back-btn" onClick={() => navigate("/login")} className="text-slate-500 hover:text-slate-800 text-sm flex items-center gap-1.5">
            <MonitorSmartphone className="w-4 h-4" />
            Verlassen
          </button>
        </div>
      </div>

      {/* Time */}
      <div className="text-center mt-4 mb-4">
        <div className="font-heading text-5xl sm:text-7xl font-extrabold tracking-tight text-slate-900 dark:text-slate-50 tabular-nums">
          {timeStr}
        </div>
        <div className="text-slate-500 text-base mt-1 capitalize">{dateStr}</div>
      </div>

      {/* Location picker */}
      {mode === "location" && (
        <div className="max-w-3xl mx-auto w-full px-6 pb-6">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-border/60 p-5">
            <h3 className="overline mb-3">Standort wählen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {locations.map((l) => (
                <button
                  key={l.id}
                  data-testid={`kiosk-loc-${l.id}`}
                  onClick={() => changeLocation(l.id)}
                  className={`text-left rounded-xl border p-4 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20 ${locationId === l.id ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30" : "border-border/60"}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: `${l.color}25`, color: l.color }}>
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-heading font-bold">{l.name}</div>
                      <div className="text-xs text-slate-500">{l.address}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Start screen: choose action */}
      {mode === "start" && (
        <div className="flex-1 flex flex-col items-center px-6 pb-8 max-w-4xl mx-auto w-full">
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <button
              data-testid="kiosk-mode-pin"
              onClick={() => setMode("pin")}
              className="rounded-2xl bg-white dark:bg-slate-900 border-2 border-border/60 hover:border-blue-500 p-6 text-left"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-700 grid place-items-center mb-3"><Delete className="w-6 h-6" /></div>
              <div className="font-heading font-bold text-lg">Nur PIN</div>
              <div className="text-sm text-slate-500">Schnellstempeln nur mit 4-6-stelliger PIN.</div>
            </button>
            <button
              data-testid="kiosk-mode-name"
              onClick={() => setMode("name")}
              className="rounded-2xl bg-white dark:bg-slate-900 border-2 border-border/60 hover:border-blue-500 p-6 text-left"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center mb-3"><UsersIcon className="w-6 h-6" /></div>
              <div className="font-heading font-bold text-lg">Mit Name auswählen</div>
              <div className="text-sm text-slate-500">Zeigt Mitarbeiter am aktuellen Standort. Danach PIN eingeben.</div>
            </button>
          </div>
          <div className="text-slate-400 text-xs">Standort: <strong>{currentLoc?.name}</strong> · {users.length} Mitarbeiter</div>
        </div>
      )}

      {/* Name selection */}
      {mode === "name" && !selectedUser && (
        <div className="flex-1 max-w-5xl mx-auto w-full px-6 pb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-lg">Mitarbeiter wählen</h3>
            <button data-testid="kiosk-name-back" onClick={() => setMode("start")} className="text-sm text-slate-500 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Zurück</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {users.map((u) => (
              <button
                key={u.id}
                data-testid={`kiosk-user-${u.id}`}
                onClick={() => { setSelectedUser(u); setMode("pin"); }}
                className="rounded-2xl bg-white dark:bg-slate-900 border border-border/60 hover:border-blue-500 p-4 flex flex-col items-center text-center"
              >
                <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-heading font-extrabold text-xl mb-2 overflow-hidden">
                  {u.avatar_b64 ? <img src={u.avatar_b64} alt="" className="w-full h-full object-cover" /> : `${u.first_name?.[0] || ""}${u.last_name?.[0] || ""}`}
                </div>
                <div className="font-semibold text-sm truncate w-full">{u.first_name} {u.last_name}</div>
                <div className="text-[10px] uppercase text-slate-500 mt-0.5">{u.position}</div>
              </button>
            ))}
            {users.length === 0 && <div className="col-span-full text-center text-slate-500 py-12">Keine Mitarbeiter an diesem Standort</div>}
          </div>
        </div>
      )}

      {/* PIN pad + actions */}
      {mode === "pin" && (
        <div className="flex-1 grid md:grid-cols-2 gap-6 max-w-5xl mx-auto w-full px-6 pb-10">
          <div>
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-sm border border-border/60">
              {selectedUser && (
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border/60">
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-bold overflow-hidden">
                    {selectedUser.avatar_b64 ? <img src={selectedUser.avatar_b64} alt="" className="w-full h-full object-cover" /> : `${selectedUser.first_name?.[0]}${selectedUser.last_name?.[0]}`}
                  </div>
                  <div className="flex-1">
                    <div className="font-heading font-bold">{selectedUser.first_name} {selectedUser.last_name}</div>
                    <div className="text-xs text-slate-500">{selectedUser.position}</div>
                  </div>
                  <button data-testid="kiosk-user-clear" onClick={() => { setSelectedUser(null); setMode("name"); setPin(""); }} className="text-xs text-blue-600 hover:underline">Ändern</button>
                </div>
              )}
              <div className="overline text-center mb-3">PIN eingeben</div>
              <div className="flex justify-center gap-3 mb-6" data-testid="kiosk-pin-display">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className={`w-10 h-12 rounded-md border-2 grid place-items-center text-2xl font-bold transition-colors ${i < pin.length ? "border-blue-700 bg-blue-50 dark:bg-blue-950" : "border-slate-200 dark:border-slate-700"}`}>
                    {i < pin.length ? "•" : ""}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {NUMBERS.map((k) => (
                  <button
                    key={k}
                    data-testid={`kiosk-key-${k}`}
                    onClick={() => handleKey(k)}
                    className="kiosk-key text-slate-900 dark:text-slate-100 dark:bg-slate-800 dark:border-slate-700"
                  >
                    {k === "⌫" ? <Delete className="mx-auto w-6 h-6" /> : k}
                  </button>
                ))}
              </div>
              <button onClick={resetToStart} className="w-full mt-3 text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 justify-center py-2">
                <ArrowLeft className="w-4 h-4" /> Zurück zum Start
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {ACTIONS.map((a) => (
              <button
                key={a.id}
                data-testid={`kiosk-action-${a.id}`}
                disabled={pin.length < 4}
                onClick={() => submit(a.id)}
                className={`rounded-3xl p-6 text-white font-bold shadow-lg disabled:opacity-40 disabled:cursor-not-allowed bg-gradient-to-br ${a.color} h-full flex flex-col items-center justify-center gap-3 ${action === a.id ? "scale-95" : ""}`}
                style={{ transition: "transform 0.1s ease, opacity 0.2s ease" }}
              >
                <a.icon className="w-10 h-10" />
                <div className="text-2xl">{a.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!candidates} onOpenChange={() => setCandidates(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Wer bist du?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 mb-2">Diese PIN wird von mehreren Mitarbeitern verwendet. Bitte wähle deinen Namen:</p>
          <div className="grid grid-cols-2 gap-3">
            {candidates?.map((c) => (
              <button
                key={c.id}
                data-testid={`kiosk-candidate-${c.id}`}
                onClick={() => submitWithCandidate(c.id)}
                className="rounded-xl border border-border/60 hover:border-blue-500 p-3 flex items-center gap-3 text-left"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-bold">{c.first_name?.[0]}{c.last_name?.[0]}</div>
                <div>
                  <div className="font-semibold">{c.first_name} {c.last_name}</div>
                  <div className="text-xs text-slate-500">{c.position}</div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
